import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { FormPrevious, FormNext } from 'grommet-icons';
import uniqid from 'uniqid';
import { AppButton, Spinning } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { OnboardingTermsSource } from '@RedflagAI/node-utility/interface';
import OnboardingStepper from '../../Onboarding/OnboardingStepper';
import {
  Step1AccountDetails,
  Step2ProductSelection,
  Step3PlansConfig,
  Step4PricingSummary,
  Step5Terms,
  Step6Review,
  Step7GeneratedLink,
} from './Steps';

const STEP_KEYS = {
  ACCOUNT: 'account',
  PRODUCTS: 'products',
  PLANS: 'plans',
  PRICING: 'pricing',
  TERMS: 'terms',
  REVIEW: 'review',
};

const STEPS = [
  { key: STEP_KEYS.ACCOUNT, label: 'Account Details' },
  { key: STEP_KEYS.PRODUCTS, label: 'Product Selection' },
  { key: STEP_KEYS.PLANS, label: 'Plans & Config' },
  { key: STEP_KEYS.PRICING, label: 'Pricing & Extras' },
  { key: STEP_KEYS.TERMS, label: 'Terms' },
  { key: STEP_KEYS.REVIEW, label: 'Review & Confirm' },
];

const STEP_ORDER = STEPS.map((s) => s.key);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const INITIAL_CONTACT = {
  email: '', fullName: '', companyName: '', phone: '', country: '',
};

const INITIAL_TERMS = {
  source: OnboardingTermsSource.DEFAULT, bodyBase64: '', uploadedFileName: '', customMode: 'upload',
};

const AdminPrefillWizard = ({
  small,
  size,
  loading,
  submitLoading,
  pricingData = null,
  prefillResult = null,
  onSubmit,
  onReset = null,
}) => {
  const { theme: { page, button } } = useAppTheme();
  const [activeStep, setActiveStep] = React.useState(STEP_KEYS.ACCOUNT);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [visitedSteps, setVisitedSteps] = React.useState(new Set([STEP_KEYS.ACCOUNT]));
  const [activeSubStep, setActiveSubStep] = React.useState(0);
  const [submitError, setSubmitError] = React.useState(null);

  const [contact, setContact] = React.useState({ ...INITIAL_CONTACT });
  const [accountType, setAccountType] = React.useState('');
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [productConfigs, setProductConfigs] = React.useState([]);
  const [extraItems, setExtraItems] = React.useState([]);
  const [terms, setTerms] = React.useState({ ...INITIAL_TERMS });

  React.useEffect(() => {
    if (prefillResult?.url) setShowSuccess(true);
  }, [prefillResult]);

  const navigateTo = (key) => {
    setActiveStep(key);
    setVisitedSteps((prev) => new Set([...prev, key]));
    if (key !== STEP_KEYS.PLANS) setActiveSubStep(0);
  };

  const handleProductAdd = (product, defaultCfg) => {
    setSelectedProducts((prev) => [...prev, product]);
    setProductConfigs((prev) => [...prev, defaultCfg]);
  };

  const handleProductRemove = (idx) => {
    setSelectedProducts((prev) => prev.filter((_, i) => i !== idx));
    setProductConfigs((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateProductConfig = (idx, cfg) => {
    setProductConfigs((prev) => prev.map((c, i) => (i === idx ? cfg : c)));
  };

  const isStep1Complete = Boolean(isValidEmail(contact.email) && accountType);

  const isProductConfigComplete = React.useCallback((idx) => {
    const product = selectedProducts[idx];
    const config = productConfigs[idx];
    if (!product || !config) return false;

    if (product.productType === 'cid') {
      const channels = (config.config?.channels || []).filter((c) => c.trim());
      if (channels.length === 0) return false;
    }
    if (product.productType === 'social') {
      if ((config.config?.platforms || []).length === 0) return false;
      // Social is always custom plan — needs a plan name
      return Boolean(config.customPlan?.name?.trim());
    }

    if (config.planMode === 'existing') return Boolean(config.planId);
    return Boolean(config.customPlan?.name?.trim());
  }, [selectedProducts, productConfigs]);

  const allProductsConfigured = selectedProducts.length > 0
    && selectedProducts.every((_, i) => isProductConfigComplete(i));

  const isExtraItemsValid = extraItems.every(
    (item) => item.description.trim() && item.value !== '' && parseFloat(item.value) >= 0,
  );

  const isTermsValid = terms.source === OnboardingTermsSource.DEFAULT
    || Boolean(terms.bodyBase64);

  const canGoNext = () => {
    if (activeStep === STEP_KEYS.ACCOUNT) return isStep1Complete;
    if (activeStep === STEP_KEYS.PRODUCTS) return selectedProducts.length > 0;
    if (activeStep === STEP_KEYS.PLANS) return allProductsConfigured;
    if (activeStep === STEP_KEYS.PRICING) return isExtraItemsValid;
    if (activeStep === STEP_KEYS.TERMS) return isTermsValid;
    return false;
  };

  const goNext = () => {
    const idx = STEP_ORDER.indexOf(activeStep);
    if (idx < STEP_ORDER.length - 1) navigateTo(STEP_ORDER[idx + 1]);
  };

  const goBack = () => {
    const idx = STEP_ORDER.indexOf(activeStep);
    if (idx > 0) navigateTo(STEP_ORDER[idx - 1]);
  };

  const handleEditProduct = (productIdx) => {
    setActiveSubStep(productIdx);
    navigateTo(STEP_KEYS.PLANS);
  };

  /* eslint-disable camelcase */
  const buildConfig = (type, cfg) => {
    if (type === 'cid') {
      return {
        uploader: cfg.customPlan?.data?.uploader || cfg.config?.uploader || 'redflag',
        channels: (cfg.config?.channels || []).filter((c) => c.trim()),
      };
    }
    if (type === 'social') {
      return {
        platforms: cfg.config?.platforms || [],
        ...(cfg.config?.mainPlatform && { main_platform: cfg.config.mainPlatform }),
        ...(cfg.config?.mainLink && { main_link: cfg.config.mainLink }),
      };
    }
    return {};
  };

  const buildPlanData = (type, cfg) => {
    const data = cfg.customPlan?.data || {};
    if (type === 'cid') {
      return {
        uploader: data.uploader || 'redflag',
        ...(data.planType && { plan_type: data.planType }),
        setup_fee_amount: parseFloat(data.setupFeeAmount) || 0,
        monthly_fee_amount: parseFloat(data.monthlyFeeAmount) || 0,
        revenue_share_percent: parseFloat(data.revenueSharePercent) || 0,
        revenue_share_start_month: parseInt(data.revenueShareStartMonth, 10) || 0,
      };
    }
    if (type === 'social') {
      return {
        default_per_platform_amount: parseFloat(data.defaultPerPlatformAmount) || 10,
        platform_overrides: (data.platformOverrides || []).map((o) => ({
          platform: o.platform,
          amount: parseFloat(o.amount) || 0,
        })),
      };
    }
    const result = {
      frequency: data.frequency || 'monthly',
      amount: parseFloat(data.amount) || 0,
    };
    if (data.setupFee !== '' && data.setupFee !== undefined && data.setupFee !== null) {
      result.setup_fee = parseFloat(data.setupFee) || 0;
    }
    return result;
  };

  const buildPayload = () => ({
    contact: {
      email: contact.email.trim(),
      ...(contact.fullName.trim() && { full_name: contact.fullName.trim() }),
      ...(contact.companyName.trim() && { company_name: contact.companyName.trim() }),
      ...(contact.phone.trim() && { phone: contact.phone.trim() }),
      ...(contact.country && { country: contact.country }),
    },
    account_type: accountType,
    products: selectedProducts.map((p, i) => {
      const cfg = productConfigs[i];
      const planBlock = cfg.planMode === 'existing'
        ? { plan_id: cfg.planId }
        : {
          custom_plan: {
            name: cfg.customPlan.name,
            ...(cfg.customPlan.description && { description: cfg.customPlan.description }),
            data: buildPlanData(p.productType, cfg),
          },
        };
      const productRef = (p.mode === 'existing' && p.productId)
        ? { product_id: p.productId }
        : { new_product: { ...(p.newProduct || { title: p.productLabel }), type: p.productType } };
      return {
        ...productRef,
        config: buildConfig(p.productType, cfg),
        ...planBlock,
      };
    }),
    extra_items: extraItems.map((item) => ({
      kind: item.kind,
      value_type: item.valueType,
      value: parseFloat(item.value) || 0,
      description: item.description,
      frequency: item.frequency,
      product_key: item.productKey || null,
    })),
    terms: {
      source: terms.source,
      ...(terms.source === OnboardingTermsSource.CUSTOM
        ? { body_base64: terms.bodyBase64 }
        : {}),
    },
  });
  /* eslint-enable camelcase */

  const handleSubmit = () => {
    setSubmitError(null);
    onSubmit(buildPayload(), (err) => {
      const msg = err?.statusCode === 409
        ? 'An active onboarding already exists for this email.'
        : (err?.message || 'Submission failed. Please try again.');
      setSubmitError(msg);
    });
  };

  const handleCreateAnother = () => {
    setContact({ ...INITIAL_CONTACT });
    setAccountType('');
    setSelectedProducts([]);
    setProductConfigs([]);
    setExtraItems([]);
    setTerms({ ...INITIAL_TERMS });
    setActiveStep(STEP_KEYS.ACCOUNT);
    setActiveSubStep(0);
    setVisitedSteps(new Set([STEP_KEYS.ACCOUNT]));
    setSubmitError(null);
    setShowSuccess(false);
    if (onReset) onReset();
  };

  const disabledSteps = {
    [STEP_KEYS.PRODUCTS]: !isStep1Complete && !visitedSteps.has(STEP_KEYS.PRODUCTS),
    [STEP_KEYS.PLANS]: (!isStep1Complete || selectedProducts.length === 0) && !visitedSteps.has(STEP_KEYS.PLANS),
    [STEP_KEYS.PRICING]: (!isStep1Complete || !allProductsConfigured) && !visitedSteps.has(STEP_KEYS.PRICING),
    [STEP_KEYS.TERMS]: (!isStep1Complete || !allProductsConfigured) && !visitedSteps.has(STEP_KEYS.TERMS),
    [STEP_KEYS.REVIEW]: !isStep1Complete || !allProductsConfigured || !isTermsValid,
  };

  const stepperSteps = STEPS.map((s) => ({
    ...s,
    disabled: Boolean(disabledSteps[s.key]),
    tooltip: disabledSteps[s.key] ? 'Complete previous steps first' : '',
  }));

  const renderStepContent = () => {
    if (showSuccess) {
      return (
        <Step7GeneratedLink
          prefillResult={prefillResult}
          onCreateAnother={handleCreateAnother}
        />
      );
    }
    if (activeStep === STEP_KEYS.ACCOUNT) {
      return (
        <Step1AccountDetails
          small={small}
          contact={contact}
          setContact={setContact}
          accountType={accountType}
          setAccountType={setAccountType}
        />
      );
    }
    if (activeStep === STEP_KEYS.PRODUCTS) {
      return (
        <Step2ProductSelection
          small={small}
          pricingData={pricingData}
          selectedProducts={selectedProducts}
          onProductAdd={handleProductAdd}
          onProductRemove={handleProductRemove}
        />
      );
    }
    if (activeStep === STEP_KEYS.PLANS) {
      return (
        <Step3PlansConfig
          small={small}
          pricingData={pricingData}
          selectedProducts={selectedProducts}
          productConfigs={productConfigs}
          updateProductConfig={updateProductConfig}
          extraItems={extraItems}
          setExtraItems={setExtraItems}
          activeSubStep={activeSubStep}
          setActiveSubStep={setActiveSubStep}
          accountType={accountType}
        />
      );
    }
    if (activeStep === STEP_KEYS.PRICING) {
      return (
        <Step4PricingSummary
          small={small}
          selectedProducts={selectedProducts}
          productConfigs={productConfigs}
          extraItems={extraItems}
          setExtraItems={setExtraItems}
          onEditProduct={handleEditProduct}
        />
      );
    }
    if (activeStep === STEP_KEYS.TERMS) {
      return <Step5Terms terms={terms} setTerms={setTerms} accountType={accountType} />;
    }
    if (activeStep === STEP_KEYS.REVIEW) {
      return (
        <Step6Review
          contact={contact}
          accountType={accountType}
          selectedProducts={selectedProducts}
          productConfigs={productConfigs}
          extraItems={extraItems}
          terms={terms}
          onSubmit={handleSubmit}
          submitLoading={submitLoading}
          submitError={submitError}
        />
      );
    }
    return null;
  };

  return (
    <Box gap="1rem" width="100%">
      <Box>
        <Text size="1.4rem" weight={700} color={page.primaryText}>
          Generate Customer Onboarding Link
        </Text>
        <Text size="0.875rem" color={page.secondaryText} margin={{ top: '0.25rem' }}>
          Create a prefilled onboarding link to send to a prospective customer.
        </Text>
      </Box>
      {!showSuccess && (
        <Box background={page.altBg} pad="0.75rem" round="var(--app-radius)">
          <OnboardingStepper
            key={uniqid()}
            small={small}
            size={size}
            steps={stepperSteps}
            activeKey={activeStep}
            onStepClick={(key) => { if (!disabledSteps[key]) navigateTo(key); }}
          />
        </Box>
      )}
      <Box
        pad="1rem"
        background={page.altBg}
        round="var(--app-radius)"
        style={{ minHeight: '300px' }}
      >
        {loading && !pricingData ? (
          <Box fill align="center" justify="center">
            <Spinning size="large" />
          </Box>
        ) : (
          renderStepContent()
        )}
      </Box>
      {!showSuccess && activeStep !== STEP_KEYS.REVIEW && (
        <Box direction="row" justify="between" align="center">
          <Box>
            {activeStep !== STEP_KEYS.ACCOUNT && (
              <AppButton
                overrideHover
                onClick={goBack}
                level="dynamicSmall"
                bgColor={page.altBg}
                color={page.primaryText}
                borderColor={button.border}
                fontWeight={600}
                style={{ border: `1px solid ${button.border}` }}
                label={(
                  <Box direction="row" gap="0.25rem" align="center" pad={{ right: '0.5rem' }}>
                    <FormPrevious size="1.25rem" color={page.primaryText} />
                    <Text color={page.primaryText}>Back</Text>
                  </Box>
                )}
              />
            )}
          </Box>
          <AppButton
            overrideHover
            onClick={goNext}
            disabled={!canGoNext()}
            level="dynamicSmall"
            color="white"
            bgColor={button.primaryBg}
            fontWeight={600}
            label={(
              <Box direction="row" gap="0.25rem" align="center" pad={{ left: '0.5rem' }}>
                <Text color="white">Next</Text>
                <FormNext size="1.25rem" color="white" />
              </Box>
            )}
          />
        </Box>
      )}
      {!showSuccess && activeStep === STEP_KEYS.REVIEW && (
        <Box>
          <AppButton
            overrideHover
            onClick={goBack}
            level="dynamicSmall"
            bgColor={page.altBg}
            color={page.primaryText}
            borderColor={button.border}
            fontWeight={600}
            style={{ border: `1px solid ${button.border}` }}
            label={(
              <Box direction="row" gap="0.25rem" align="center" pad={{ right: '0.5rem' }}>
                <FormPrevious size="1.25rem" color={page.primaryText} />
                <Text color={page.primaryText}>Back</Text>
              </Box>
            )}
          />
        </Box>
      )}
    </Box>
  );
};

AdminPrefillWizard.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  pricingData: PropTypes.object,
  prefillResult: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
};

export default AdminPrefillWizard;
